import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/client";
import Header from "../../components/layout/Header";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Download, FilterX, Search, Trash2 } from "lucide-react";
import { formatDateTimeBR } from "../../utils/format";
import EmptyState from "../../components/common/EmptyState";
import { toast } from "sonner";

const typeLabel = {
  COMPROVACAO_PLANO_ACAO: "Comprovação do plano de ação",
  PRE: "PRE",
  PGRS: "PGRS (histórico)",
  RELATORIO_VISITA: "Relatório de visita",
  LEVANTAMENTO_PRE_PROPOSTA: "Levantamento pré-proposta",
};

export default function DocumentsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("todos");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      setItems((await api.get("/documents")).data);
    } catch (e) {
      toast.error("Falha ao carregar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const download = async (id, tipo) => {
    try {
      const r = await api.get(`/documents/${id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const doc = items.find((x) => x.id === id);
      const cli = (doc?.client_snapshot?.razao_social || "cliente")
        .replace(/[\\/:*?"<>|]/g, "")
        .trim();
      const base =
        tipo === "RELATORIO_VISITA"
          ? "Relatorio de Visita"
          : tipo === "LEVANTAMENTO_PRE_PROPOSTA"
            ? "Levantamento Pre-Proposta"
            : tipo;
      const revision = doc?.revision_label ? ` - ${doc.revision_label}` : "";
      const a = document.createElement("a");
      a.href = url;
      a.download = `${base} - ${cli || "cliente"}${revision}.${tipo === "COMPROVACAO_PLANO_ACAO" ? "pdf" : "docx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Falha ao baixar");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este documento?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Excluído");
      load();
    } catch (e) {
      toast.error("Falha");
    }
  };

  const types = useMemo(
    () => Array.from(new Set(items.map((d) => d.tipo).filter(Boolean))).sort(),
    [items],
  );
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((d) => {
      if (typeFilter !== "todos" && d.tipo !== typeFilter) return false;
      const date = String(d.data_emissao || d.created_at || "").slice(0, 10);
      if (fromDate && date && date < fromDate) return false;
      if (toDate && date && date > toDate) return false;
      if (!term) return true;
      const hay = [
        d.tipo,
        typeLabel[d.tipo],
        d.titulo,
        d.nome,
        d.codigo,
        d.revision_label,
        d.client_snapshot?.razao_social,
        d.client_snapshot?.nome_fantasia,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });
  }, [items, search, typeFilter, fromDate, toDate]);

  const hasFilters = Boolean(
    search.trim() || typeFilter !== "todos" || fromDate || toDate,
  );
  const clearFilters = () => {
    setSearch("");
    setTypeFilter("todos");
    setFromDate("");
    setToDate("");
  };

  return (
    <>
      <Header
        title="Documentos gerados"
        subtitle={`${items.length} documento(s) gerado(s)`}
      />
      <div
        className="page-container space-y-5"
        data-testid="documents-list-page"
      >
        <div className="surface-panel space-y-4 p-4 sm:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="label-tech">Histórico documental</div>
              <div className="mt-1 text-sm font-medium">
                {filtered.length} de {items.length} documento(s)
              </div>
            </div>
            <div className="relative w-full lg:max-w-xl">
              <Search className="absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                placeholder="Cliente, tipo, código ou revisão..."
                aria-label="Buscar documentos"
              />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_180px_180px_auto]">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="h-10 rounded-xl border border-input bg-background px-3 text-sm outline-none transition focus:border-primary"
              aria-label="Filtrar por tipo de documento"
            >
              <option value="todos">Todos os tipos</option>
              {types.map((type) => (
                <option key={type} value={type}>
                  {typeLabel[type] || type}
                </option>
              ))}
            </select>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              aria-label="Data inicial"
            />
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              aria-label="Data final"
            />
            <Button
              type="button"
              variant="outline"
              onClick={clearFilters}
              disabled={!hasFilters}
            >
              <FilterX className="size-4" /> Limpar filtros
            </Button>
          </div>
        </div>

        {loading && (
          <div className="text-muted-foreground text-sm">Carregando...</div>
        )}
        {!loading && filtered.length === 0 && (
          <EmptyState
            testid="documents-empty"
            title={
              hasFilters
                ? "Nenhum documento encontrado"
                : "Nenhum documento gerado ainda"
            }
            description={
              hasFilters
                ? "Ajuste os filtros ou limpe a busca."
                : "PREs, relatórios de visita e levantamentos pré-proposta emitidos pelo CEO aparecerão aqui."
            }
            action={
              hasFilters ? (
                <Button variant="outline" onClick={clearFilters}>
                  <FilterX className="size-4" /> Limpar filtros
                </Button>
              ) : null
            }
          />
        )}
        {!loading && filtered.length > 0 && (
          <div className="overflow-x-auto rounded-2xl border border-border/80 bg-card/88">
            <table className="w-full text-sm">
              <thead className="bg-muted/30">
                <tr>
                  <th className="text-left px-4 py-3 label-tech">Tipo</th>
                  <th className="text-left px-4 py-3 label-tech">Cliente</th>
                  <th className="text-left px-4 py-3 label-tech">
                    Data Emissão
                  </th>
                  <th className="text-left px-4 py-3 label-tech">Gerado em</th>
                  <th className="text-right px-4 py-3 label-tech">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((d) => (
                  <tr
                    key={d.id}
                    className="hover:bg-muted/20"
                    data-testid={`doc-row-${d.id}`}
                  >
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 text-xs font-mono uppercase rounded bg-primary/10 text-primary">
                        {typeLabel[d.tipo] || d.tipo}
                      </span>
                      {d.revision_label && (
                        <div className="mt-1 text-[10px] font-mono text-muted-foreground">
                          {d.revision_label} · SHA{" "}
                          {String(d.arquivo_sha256 || "").slice(0, 10)}…
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {d.client_snapshot?.razao_social || "-"}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {d.data_emissao || "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {formatDateTimeBR(d.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => download(d.id, d.tipo)}
                        data-testid={`doc-download-${d.id}`}
                      >
                        <Download className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => del(d.id)}
                        data-testid={`doc-delete-${d.id}`}
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
